import { Button, Field, Form, TextField } from '@gyym/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import React from 'react'
import { useForm, type UseFormSetError } from 'react-hook-form'
import { z } from 'zod'

import { EMAIL_REGEX } from '@/lib/form'

export const schema = z.object({
  email: z
    .string()
    .min(1, { message: 'メールアドレスを入力してください' })
    .regex(EMAIL_REGEX, { message: '有効なメールアドレスではありません' }),
  password: z.string().min(1, { message: 'パスワードを入力してください' }),
})

export type FormValues = z.infer<typeof schema>

type RootField = { root: string }

export type SubmitContext = {
  setError: UseFormSetError<FormValues & RootField>
}

export interface LoginFormProps {
  /**
   * Initial form values
   */
  values?: Partial<FormValues>

  /**
   * Whether the form is for SNS registration
   */
  isSnsRegistration?: boolean

  /**
   * Submit handler
   */
  onSubmit: (values: FormValues, ctx: SubmitContext) => void

  /**
   * Children render prop
   */
  children?: ({
    fields,
    submit,
  }: {
    fields: React.ReactNode
    submit: React.ReactNode
  }) => React.ReactNode
}

export const LoginForm = ({ values, onSubmit, children }: LoginFormProps) => {
  const hasChildren = !!children

  const {
    register,
    setError,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
  } = useForm<FormValues & RootField>({
    // Validation will trigger on the first blur event.
    // After the first blur event, validation will trigger on every change event.
    mode: 'onTouched',
    resolver: zodResolver(schema),
    defaultValues: values,
  })

  const onSubmitHandler = React.useCallback(
    (values: FormValues) => onSubmit(values, { setError }),
    [onSubmit, setError],
  )

  const fields = (
    <>
      {errors.root?.message && (
        <Field.Messages type="error" messages={errors.root.message} />
      )}

      <TextField
        label="メールアドレス"
        {...register('email')}
        errorMessages={errors.email?.message}
        autoComplete="username"
      />

      <TextField
        label="パスワード"
        type="password"
        {...register('password')}
        errorMessages={errors.password?.message}
        autoComplete="current-password"
      />
    </>
  )

  const submit = (
    <Button
      type="submit"
      variant="brand"
      size="lg"
      fullWidth
      loading={isSubmitting}
      disabled={!isValid}
    >
      ログイン
    </Button>
  )

  return (
    <Form gap={!hasChildren ? 4 : 0} onSubmit={handleSubmit(onSubmitHandler)}>
      {hasChildren ? (
        children({ fields, submit })
      ) : (
        <>
          {fields}
          <div className="mt-6 flex w-full self-center tablet:max-w-[160px]">
            {submit}
          </div>
        </>
      )}
    </Form>
  )
}
